// Floating, draggable, minimisable VIEW-ONLY deal review panel — opened from the
// quote builder so the estimator can check the quote against the deal's
// requirements, photos and scoping files without leaving the quote (Trent, Oct 2026).
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Loader2, X, Minus, Briefcase, GripHorizontal, Maximize2, Phone, Mail, MapPin, User, FileText, Image as ImageIcon, Paperclip } from "lucide-react";
import { TimelineEvents } from "@/components/TimelineEvents";
import { STAGE_LABELS, STAGE_COLORS, FENCE_LABELS, fmtCurrency, fmtDate, fmtDateTime } from "@/lib/constants";

const AuthImg = ({ fileId, alt }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/timeline/files/${fileId}/thumbnail`, { responseType: "blob" })
      .then((res) => { url = URL.createObjectURL(res.data); setSrc(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [fileId]);
  if (!src) return <div className="w-full h-full bg-slate-100 animate-pulse" />;
  return <img src={src} alt={alt} className="w-full h-full object-cover" />;
};

const openFile = async (id) => {
  try {
    const res = await api.get(`/timeline/files/${id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { /* view-only — fail quietly */ }
};

const Row = ({ label, children }) => (
  <div className="flex items-start gap-2 text-xs">
    <span className="text-slate-400 w-24 shrink-0 uppercase tracking-wide text-[10px] pt-0.5">{label}</span>
    <span className="text-slate-800 min-w-0">{children}</span>
  </div>
);

export default function DealPeek({ dealId, onClose }) {
  const nav = useNavigate();
  const [minimised, setMinimised] = useState(false);
  const [tab, setTab] = useState("overview");
  const [pos, setPos] = useState(() => ({
    x: Math.max(8, (typeof window !== "undefined" ? window.innerWidth : 1200) - 480),
    y: 80,
  }));
  const drag = useRef(null);

  const onPointerDown = (e) => {
    drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    const move = (ev) => {
      if (!drag.current) return;
      setPos({
        x: Math.min(Math.max(8 - 380, ev.clientX - drag.current.dx), window.innerWidth - 80),
        y: Math.min(Math.max(8, ev.clientY - drag.current.dy), window.innerHeight - 60),
      });
    };
    const up = () => {
      drag.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const { data: deal, isLoading } = useQuery({
    queryKey: ["deal-peek", dealId],
    queryFn: async () => (await api.get(`/deals/${dealId}`)).data,
    enabled: !!dealId,
  });
  const { data: timeline } = useQuery({
    queryKey: ["deal-peek-timeline", dealId],
    queryFn: async () => (await api.get(`/timeline/deal/${dealId}`)).data,
    enabled: !!dealId && tab === "timeline",
  });
  const { data: files = [] } = useQuery({
    queryKey: ["deal-peek-files", dealId],
    queryFn: async () => (await api.get("/timeline/files", { params: { deal_id: dealId } })).data,
    enabled: !!dealId,
  });
  const { data: company } = useQuery({
    queryKey: ["deal-peek-company", deal?.company_id],
    queryFn: async () => (await api.get("/companies")).data.find((c) => c.id === deal.company_id),
    enabled: !!deal?.company_id,
  });
  const { data: contacts = [] } = useQuery({
    queryKey: ["deal-peek-contacts", deal?.company_id],
    queryFn: async () => (await api.get("/contacts", { params: { company_id: deal.company_id } })).data,
    enabled: !!deal?.company_id,
  });

  const { data: chat } = useQuery({
    queryKey: ["deal-peek-chat", dealId],
    queryFn: async () => (await api.get(`/deals/${dealId}/chat`)).data,
    enabled: !!dealId,
  });

  const contact = (deal?.contact_id && contacts.find((c) => c.id === deal.contact_id))
    || contacts.find((c) => c.is_primary) || contacts[0];
  const photos = files.filter((f) => (f.content_type || "").startsWith("image/"));
  const docs = files.filter((f) => !(f.content_type || "").startsWith("image/"));
  const notes = [...(chat?.messages || [])].reverse();

  if (minimised) {
    return (
      <div className="fixed bottom-4 right-4 z-[55]" data-testid="deal-peek-pill">
        <div className="flex items-center gap-2 bg-slate-900 text-white rounded-sm shadow-xl pl-3 pr-1.5 py-1.5 border border-slate-700">
          <Briefcase size={13} className="text-amber-400 shrink-0" />
          <button type="button" onClick={() => setMinimised(false)} data-testid="deal-peek-restore-btn"
            className="text-xs font-semibold truncate max-w-[180px] hover:text-amber-300">
            {deal?.title || "Deal"}
          </button>
          <button type="button" onClick={() => setMinimised(false)} className="p-1 hover:bg-slate-700 rounded-sm" aria-label="Restore deal panel">
            <Maximize2 size={12} />
          </button>
          <button type="button" onClick={onClose} data-testid="deal-peek-pill-close-btn" className="p-1 hover:bg-slate-700 rounded-sm" aria-label="Close deal panel">
            <X size={12} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed z-[55] w-[min(460px,calc(100vw-16px))] bg-white border border-slate-300 rounded-sm shadow-2xl flex flex-col"
      style={{ left: pos.x, top: pos.y, maxHeight: "min(72vh, 640px)" }}
      data-testid="deal-peek-panel"
    >
      <div
        className="flex items-center gap-2 px-3 py-2 bg-slate-900 text-white rounded-t-sm cursor-move select-none touch-none"
        onPointerDown={onPointerDown}
        data-testid="deal-peek-header"
      >
        <GripHorizontal size={13} className="text-slate-500 shrink-0" />
        <Briefcase size={13} className="text-amber-400 shrink-0" />
        <span className="text-xs font-semibold truncate flex-1">
          {deal?.title || "Deal"}{deal?.job_number ? <span className="font-mono text-amber-300 ml-2">{deal.job_number}</span> : null}
        </span>
        <span className="text-[9px] uppercase tracking-wider text-slate-400 hidden sm:inline">view only</span>
        <button type="button" onClick={() => setMinimised(true)} data-testid="deal-peek-minimise-btn"
          className="p-1 hover:bg-slate-700 rounded-sm" aria-label="Minimise deal panel">
          <Minus size={13} />
        </button>
        <button type="button" onClick={onClose} data-testid="deal-peek-close-btn"
          className="p-1 hover:bg-slate-700 rounded-sm" aria-label="Close deal panel">
          <X size={13} />
        </button>
      </div>

      <div className="flex border-b border-slate-200 bg-slate-50">
        {[["overview", "Overview"], ["notes", `Notes (${notes.length})`], ["timeline", "Timeline & files"]].map(([k, label]) => (
          <button key={k} type="button" data-testid={`deal-peek-tab-${k}`} onClick={() => setTab(k)}
            className={`px-3 py-2 text-[11px] font-semibold border-b-2 -mb-px ${tab === k ? "border-amber-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="overflow-y-auto p-3 space-y-3 flex-1">
        {isLoading ? (
          <Loader2 className="animate-spin mx-auto text-slate-400 my-8" />
        ) : !deal ? (
          <div className="text-xs text-slate-400 text-center py-6">Deal not found — it may have been deleted.</div>
        ) : tab === "overview" ? (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className={`rounded-sm text-[10px] ${STAGE_COLORS[deal.stage] || ""}`}>{STAGE_LABELS[deal.stage] || deal.stage}</Badge>
              {deal.fence_type && <Badge variant="outline" className="rounded-sm text-[10px]">{FENCE_LABELS[deal.fence_type] || deal.fence_type}</Badge>}
              {(deal.tags || []).map((t) => <Badge key={t} variant="outline" className="rounded-sm text-[10px] text-slate-500">{t}</Badge>)}
            </div>
            <div className="space-y-1.5">
              <Row label="Value">{fmtCurrency(deal.value || 0)}</Row>
              {deal.expected_close && <Row label="Quote due">{fmtDate(deal.expected_close)}</Row>}
              {company && (
                <Row label="Client">
                  <button type="button" className="text-cyan-700 hover:underline font-medium inline-flex items-center gap-1"
                    onClick={() => nav(`/clients/${company.id}`)} data-testid="deal-peek-client-link">
                    <User size={11} /> {company.name}
                  </button>
                </Row>
              )}
              {contact && <Row label="Contact">{`${contact.first_name || ""} ${contact.last_name || ""}`.trim()}</Row>}
              {(contact?.mobile_phone || contact?.phone || company?.phone) && (
                <Row label="Phone"><span className="font-mono inline-flex items-center gap-1"><Phone size={10} className="text-emerald-600" />{contact?.mobile_phone || contact?.phone || company?.phone}</span></Row>
              )}
              {(contact?.email || company?.email) && (
                <Row label="Email"><span className="inline-flex items-center gap-1 break-all"><Mail size={10} className="text-slate-400" />{contact?.email || company?.email}</span></Row>
              )}
              {(company?.billing_address || company?.address) && (
                <Row label="Address"><span className="inline-flex items-start gap-1"><MapPin size={10} className="text-slate-400 mt-0.5 shrink-0" />{company?.billing_address || company?.address}</span></Row>
              )}
            </div>
            {deal.notes && (
              <div>
                <div className="stamp-label text-slate-500 mb-1">Requirements / brief</div>
                <div className="bg-amber-50/70 border border-amber-200 rounded-sm px-2.5 py-2 text-xs text-slate-800 whitespace-pre-wrap" data-testid="deal-peek-brief">{deal.notes}</div>
              </div>
            )}
            {photos.length > 0 && (
              <div>
                <div className="stamp-label text-slate-500 mb-1 flex items-center gap-1"><ImageIcon size={11} /> Photos ({photos.length})</div>
                <div className="grid grid-cols-4 gap-1.5">
                  {photos.slice(0, 8).map((f) => (
                    <button key={f.id} type="button" onClick={() => openFile(f.id)} title={f.label || f.filename}
                      className="aspect-square rounded-sm overflow-hidden border border-slate-200 hover:ring-2 hover:ring-amber-400" data-testid={`deal-peek-photo-${f.id}`}>
                      <AuthImg fileId={f.id} alt={f.label || f.filename} />
                    </button>
                  ))}
                </div>
              </div>
            )}
            {docs.length > 0 && (
              <div>
                <div className="stamp-label text-slate-500 mb-1 flex items-center gap-1"><FileText size={11} /> Files ({docs.length})</div>
                <div className="space-y-1">
                  {docs.map((f) => (
                    <button key={f.id} type="button" onClick={() => openFile(f.id)}
                      className="w-full text-left text-xs text-cyan-700 hover:underline truncate flex items-center gap-1.5" data-testid={`deal-peek-file-${f.id}`}>
                      <FileText size={11} className="text-slate-400 shrink-0" /> {f.label || f.filename}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(deal.quotes || []).length > 1 && (
              <div>
                <div className="stamp-label text-slate-500 mb-1">Quotes on this deal</div>
                {deal.quotes.map((q) => (
                  <div key={q.id} className="text-xs text-slate-600 flex items-center gap-2">
                    <span className="font-mono">{q.quote_number}</span>
                    <Badge variant="outline" className="rounded-sm text-[9px] uppercase">{q.status}</Badge>
                    <span className="text-slate-400">{fmtCurrency(q.sale_price || 0)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : tab === "notes" ? (
          notes.length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-6">No notes on this deal yet.</div>
          ) : (
            notes.map((n) => (
              <div key={n.id} className="bg-slate-50 border border-slate-200 rounded-sm px-2.5 py-2" data-testid={`deal-peek-note-${n.id}`}>
                {n.text && <div className="text-xs text-slate-800 whitespace-pre-wrap">{n.text}</div>}
                {(n.attachments || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {n.attachments.map((a) => (
                      <button key={a.id} type="button" onClick={() => openFile(a.id)}
                        className="inline-flex items-center gap-1 text-[10.5px] text-cyan-700 underline truncate max-w-[200px]">
                        <Paperclip size={9} className="shrink-0" /> {a.label || a.filename}
                      </button>
                    ))}
                  </div>
                )}
                <div className="text-[10px] text-slate-400 mt-1 font-mono">{n.by_name} · {fmtDateTime(n.at)}</div>
              </div>
            ))
          )
        ) : !timeline ? (
          <Loader2 className="animate-spin mx-auto text-slate-400 my-8" />
        ) : (timeline.events || []).length === 0 ? (
          <div className="text-xs text-slate-400 text-center py-6">No history on this deal yet.</div>
        ) : (
          <TimelineEvents events={timeline.events} navigate={nav} readOnly />
        )}
      </div>
    </div>
  );
}
